<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { Team, Club, TeamMember, TeamUser } from '../types';
  import SearchableSelect from './SearchableSelect.svelte';
  import InvitationModal from './InvitationModal.svelte';
  import ConfirmationModal from './ConfirmationModal.svelte';
  import { showToast } from '../lib/toast';
  import * as apiService from '../lib/apiService';
  import { createEventDispatcher, onMount } from 'svelte';
  import { actionFlags, actionFlagHelpers } from '../stores/actionFlags';

  const dispatch = createEventDispatcher();

  export let team: Team | null = null; // null for new team, Team object for editing

  let dialogElement: HTMLDialogElement;
  let isAnimating = false;

  onMount(() => {
    if (dialogElement) {
      // Trigger slide-up animation after a brief delay
      setTimeout(() => {
        dialogElement.classList.add('slide-up');
      }, 10);
    }
  });

  function onClose() {
    if (isAnimating) return;

    isAnimating = true;
    dialogElement.classList.remove('slide-up');
    dialogElement.classList.add('slide-down');

    // Wait for animation to complete before closing
    setTimeout(() => {
      dispatch('cancel');
    }, 300);
  }

  // Form state
  let selectedClubId = '';
  let selectedSportsType = 'hockey';
  let clubs: Club[] = [];
  let saveTimer: ReturnType<typeof setTimeout> | null = null;

  // Team members state
  let teamMembers: TeamMember[] = [];
  let newMemberName = '';

  // Team users state
  let teamUsers: TeamUser[] = [];
  let showInvitationModal = false;
  let invitationLink = '';
  let showRemoveUserConfirmation = false;
  let userToRemove: TeamUser | null = null;

  // Sports types
  interface SportType {
    label: string;
    value: string;
  }
  
  const sportsTypes: SportType[] = [
    {label: $_('sports.field_hockey'), value: 'hockey'}, 
    {label: $_('sports.football'), value: 'football'}
  ];

  // @ts-ignore - Vite-specific type that TypeScript doesn't know about
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();

  // Reset form for new team
  selectedClubId = '';
  selectedSportsType = 'hockey';
  teamMembers = [];
  teamUsers = [];
  loadClubs(selectedSportsType);
  actionFlagHelpers.clearFlags(['isUpdatingTeam', 'isCreatingTeam']); 
  
  async function initializeTeam() {
    selectedSportsType = team.club?.sport || 'hockey';
    await loadClubs(selectedSportsType);
    
    // Set club ID and team members immediately after clubs are loaded
    selectedClubId = team.club_id;
    teamMembers = [...team.team_members];
    
    actionFlagHelpers.clearFlags(['isUpdatingTeam', 'isCreatingTeam']); 
    
    // Load team users if team exists and user has admin rights
    if (team.id && team.user_role === 'ADMIN') {
      loadTeamUsers();
    }
  }


  // Initialize form with team data if editing
  $: if (team.id) {
    initializeTeam();
  } 

  // // Reactive statement to handle club selection when clubs are loaded
  // $: if (clubs.length > 0 && team && team.club_id && selectedClubId !== team.club_id) {
  //   // Only set the club ID if it exists in the current clubs list
  //   const clubExists = clubs.some(club => club.id === team.club_id);
  //   if (clubExists) {
  //     selectedClubId = team.club_id;
  //   }
  // }

  async function loadClubs(sport: string) {
    try {
      await apiService.getPublicClubs(sport, (result, status) => {
        if (status === 200) {
          clubs = result;
        }
      });
    } catch (error) {
      console.error('Error loading clubs:', error);
    }
  }

  function onSelectSport(event: Event) {
    const target = event.target as HTMLSelectElement;
    selectedSportsType = target.value;
    selectedClubId = ''; // Reset club selection when sport changes
    loadClubs(selectedSportsType);
  }

  function onSelectClub(event: CustomEvent) {
    selectedClubId = event.detail.value;
    handleUpdate();
  }

  //Add a teammember
  function addTeamMember() {
    if ($actionFlags.isAddingTeamMember) return;
    
    const name = newMemberName.trim();
    if (!name) {
      showToast($_('settings.teamMembers.errors.enterName'), 'error');
      return;
    }
    dispatch('addTeamMember', {
      team: team, 
      name: name,
      onSuccess: () => {
        newMemberName = ''; // Clear the input
        actionFlagHelpers.clearFlag('isAddingTeamMember');
      },
      onError: () => {
        actionFlagHelpers.clearFlag('isAddingTeamMember');
      }
    });
    actionFlagHelpers.setFlag('isAddingTeamMember');
  }

  // REeove a teammember
  function removeTeamMember(index: number) {
    if ($actionFlags.isRemovingTeamMember) return;

    dispatch('removeTeamMember', {
      team: team, 
      index: index,
      onSuccess: () => {
        actionFlagHelpers.clearFlag('isRemovingTeamMember');
      },
      onError: () => {
        actionFlagHelpers.clearFlag('isRemovingTeamMember');
      }
    });
    actionFlagHelpers.setFlag('isRemovingTeamMember');
  }

  // Create a new team 
  async function createTeam() {

    // Check required fields, if missing fail silently
    if (!team.name.trim()) {
      return;
    }
    if (!selectedClubId) {
      return;
    }

    try {
      // Create new team
      const newTeam = {
        club_id: selectedClubId,
        name: team.name
      };
      dispatch('create', newTeam);
      actionFlagHelpers.setFlag('isCreatingTeam');
    } catch (error) {
      console.error('Error saving team:', error);
      showToast(team ? $_('settings.updateTeamError') : $_('settings.addTeam.error'), 'error');
    }
  }

  // Handle save with debouncing, prevent too fast updates
  function handleUpdate() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => updateTeam(), 1000);
  }
  
  // Save team settings
  async function updateTeam() {

    // Check required fields, if missing fail silently
    if (!team.name.trim()) {
      return;
    }
    if (!selectedClubId) {
      return;
    }

    try {
      if (team.id) {
        // Update existing team
        const updatedTeam = {
          ...team,
          club_id: selectedClubId,
          team_members: teamMembers
        };
        dispatch('update', updatedTeam);
        actionFlagHelpers.setFlag('isUpdatingTeam');
      } 
    } catch (error) {
      console.error('Error saving team:', error);
      showToast(team ? $_('settings.updateTeamError') : $_('settings.addTeam.error'), 'error');
    }
  }

  // Team users management functions
  async function loadTeamUsers() {
    if (!team?.id || $actionFlags.isLoadingTeamUsers) return;
    
    actionFlagHelpers.setFlag('isLoadingTeamUsers');
    try {
      await apiService.getTeamUsers(team.id, (result, status) => {
        if (status === 200) {
          teamUsers = result.team_users;
        } else {
          console.error('Error loading team users:', result);
        }
      });
    } catch (error) {
      console.error('Error loading team users:', error);
    } finally {
      actionFlagHelpers.clearFlag('isLoadingTeamUsers');
    }
  }

  async function inviteUser() {
    if (!team?.id || $actionFlags.isInvitingUser) return;
    
    actionFlagHelpers.setFlag('isInvitingUser');
    try {
      await apiService.inviteTeamUser(team.id, (result, status) => {
        if (status === 200) {
          invitationLink = result.invitation_link;
          showInvitationModal = true;
        } else {
          showToast(result?.message || $_('settings.teamUsers.errors.inviteError'), 'error');
        }
      });
    } catch (error) {
      console.error('Error inviting user:', error);
      showToast($_('settings.teamUsers.errors.inviteError'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isInvitingUser');
    }
  }

  function handleDeleteTeam() {
    if (!team?.id) return;
    dispatch('delete', team);
  }

  function handleRemoveUser(user: TeamUser) {
    userToRemove = user;
    showRemoveUserConfirmation = true;
  }

  async function confirmRemoveUser() {
    if (!team?.id || !userToRemove || $actionFlags.isRemovingUser) return;
    
    actionFlagHelpers.setFlag('isRemovingUser');
    try {
      await apiService.removeTeamUser(team.id, userToRemove.user_id, (result, status) => {
        if (status === 200) {
          showToast($_('settings.teamUsers.success.removed', { values: { name: userToRemove?.username } }));
          loadTeamUsers(); // Reload team users
        } else {
          showToast(result?.message || $_('settings.teamUsers.errors.removeError'), 'error');
        }
      });
    } catch (error) {
      console.error('Error removing user:', error);
      showToast($_('settings.teamUsers.errors.removeError'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isRemovingUser');
      showRemoveUserConfirmation = false;
      userToRemove = null;
    }
  }

  function cancelRemoveUser() {
    showRemoveUserConfirmation = false;
    userToRemove = null;
  }

  function closeInvitationModal() {
    showInvitationModal = false;
    invitationLink = '';
  }

  // Accordion state
  let teamMembersExpanded = true;
  let teamUsersExpanded = false;
</script>

<dialog bind:this={dialogElement} open class="slide-up-dialog full-height">
  <article class="mt-0">
    <header class="py-4 mb-6">
      <h5>{team?.id ? $_('settings.editTeam.title') : $_('settings.addTeam.title')}</h5>
      <button class="close-button" on:click={onClose}>×</button>
    </header>

    {#if team?.id}
      <div class="flex gap-2 flex-row items-center mb-6">
        <img class="team-logo" src={team?.club?.logo_filename ? `/img/clublogos/${team?.club?.logo_filename}?v=${BUILD_TIMESTAMP}` : '/img/clublogos/default.png'} alt={team?.club?.name} >
        <h5>{team?.club?.name}</h5>
      </div>
    {/if}

      <form on:submit|preventDefault={handleUpdate} class="space-y-4">

      {#if !team?.id}
        <div>
          <label for="sportsType">{$_('signup.sports_type')}</label>
          <select 
            id="sportsType"
            value={selectedSportsType}
            on:change={onSelectSport}
            required
          >
            {#each sportsTypes as sport}
              <option value={sport.value}>{sport.label}</option>
            {/each}
          </select>
        </div>
        
        <!-- Club Selection and Team Name -->
          <div>
            <label for="clubSelect">{$_('settings.addTeam.club')}</label>
            <SearchableSelect 
              id="clubSelect"
              options={clubs.map(club => ({ value: club.id, label: club.name, image: club.logo_filename ? `/img/clublogos/${club.logo_filename}?v=${BUILD_TIMESTAMP}` : null }))}
              value={selectedClubId}
              on:change={onSelectClub}
              placeholder={$_('settings.addTeam.selectClub')}
            />
          </div>
        {/if}
        <div>
            <label for="teamName">{$_('settings.addTeam.teamName')}</label>
            <input 
              id="teamName"
              type="text" 
              bind:value={team.name}
              on:change={handleUpdate}
              placeholder={$_('settings.addTeam.teamNamePlaceholder')}
              required
            />
          </div>

        <!-- Default Meeting Location -->
        <div>
          <label for="defaultMeetingLocation">{$_('settings.default_meeting_location')}</label>
          <input 
            type="text" 
            id="defaultMeetingLocation" 
            bind:value={team.default_meeting_location}
            on:change={handleUpdate}
            placeholder={$_('settings.default_meeting_location_placeholder')} 
          />
        </div>

        <!-- Team Members Section -->
        <div class="my-6">
          <hr>
          <h4 class="accordion-header clickable" on:click={() => teamMembersExpanded = !teamMembersExpanded}>
            <span><ion-icon name="people-outline"></ion-icon> {$_('settings.teamMembers.existing_members')} ({teamMembers.length})</span>
            <ion-icon name={teamMembersExpanded ? "chevron-up-outline" : "chevron-down-outline"}></ion-icon>
          </h4>

          {#if teamMembersExpanded}
          {#if teamMembers.length === 0}
            <p class="text text-sm text-muted text-italic">{$_('settings.no_players_yet')}</p>
          {:else}
            {#each teamMembers as member, index}
              <div class="member-row px-4">
                {member.name}
                <button 
                  type="button"
                  class="small secondary outline ghost mb-0" 
                  on:click={() => removeTeamMember(index)}
                >
                  <ion-icon name="trash-outline"></ion-icon>
                </button>
              </div>
            {/each}
          {/if}

          <!-- Add Team Member form -->
          <div class="add-member-form mt-4">
            <label for="newMember">{$_('settings.teamMembers.newPlayerLabel')}</label>
            <div class="grid grid-2">
              <input 
                id="newMember"
                type="text" 
                bind:value={newMemberName} 
                placeholder={$_('settings.teamMembers.addNew')} 
              />
              <button 
                type="button" 
                on:click={addTeamMember} 
                disabled={!newMemberName || $actionFlags.isAddingTeamMember}
                class="small outline"
              >
                <ion-icon name="add-circle"></ion-icon>
                {$actionFlags.isAddingTeamMember ? $_('settings.teamMembers.adding') : $_('settings.teamMembers.addButton')}
              </button>
            </div>
          </div>
          {/if}
        </div>

        <!-- Team Users Section (Admin only) -->
        {#if team && team.id && team.user_role === 'ADMIN'}
        <div class="my-6">
          <hr>
          <h4 class="accordion-header clickable" on:click={() => teamUsersExpanded = !teamUsersExpanded}>
            <span><ion-icon name="people-circle-outline"></ion-icon> {$_('settings.teamUsers.title')} ({teamUsers.length})</span>
            <ion-icon name={teamUsersExpanded ? "chevron-up-outline" : "chevron-down-outline"}></ion-icon>
          </h4>

          {#if teamUsersExpanded}
          {#if $actionFlags.isLoadingTeamUsers}
            <p>{$_('settings.teamUsers.loading')}</p>
          {:else if teamUsers.length === 0}
            <p>{$_('settings.teamUsers.noUsers')}</p>
          {:else}
            {#each teamUsers as user}
              <div class="member-row flex justify-between px-4">
                {user.username} 
                {#if user.role === 'ADMIN'}
                <ion-icon name="star-outline"></ion-icon>
                {:else}
                  <button 
                    type="button"
                    class="small secondary outline ghost mb-0" 
                    on:click={() => handleRemoveUser(user)}
                    disabled={$actionFlags.isRemovingUser}
                  >
                    <ion-icon name="trash-outline"></ion-icon>
                  </button>
                {/if}
              </div>
            {/each}
          {/if}

          <!-- Invite User form -->
          <div class="add-member-form mt-4">
            <div class="flex justify-between align-center">
              <span>{$_('settings.teamUsers.totalUsers', { values: { count: teamUsers.length, max: 25 } })}</span>
              <button 
                type="button" 
                on:click={inviteUser} 
                disabled={$actionFlags.isInvitingUser || teamUsers.length >= 25}
                class="small outline"
              >
                <ion-icon name="add-circle"></ion-icon>
                {$actionFlags.isInvitingUser ? $_('settings.teamUsers.inviting') : $_('settings.teamUsers.inviteButton')}
              </button>
            </div>
          </div>
          {/if}
        </div>
        {/if}

        <footer>
        <!-- Action Buttons -->
          {#if !team.id}
          <button 
            on:click={createTeam}
            disabled={$actionFlags.isCreatingTeam}
            class="mt-4 full-width"
          >
            {$_('settings.addTeam.saveButton')}{$actionFlags.isCreatingTeam ? ' ...' : ''}
          </button>
          {:else}
            <button 
              on:click={onClose}
              class="primary mt-4 full-width"
            >
              {$_('settings.teams.closeButton')}
            </button>
            <hr class="my-6"/>
            <div class="align-right full-width mt-6">
              <button type="button" class="secondary ghost small" on:click|preventDefault={() => handleDeleteTeam()}>
              <ion-icon name="trash-outline"></ion-icon>
              {$_('common.delete')}
              </button>
            </div>
          {/if}
        </footer>
      </form>
</article>
</dialog>

<!-- Invitation Modal -->
<InvitationModal
  isOpen={showInvitationModal}
  invitationLink={invitationLink}
  onClose={closeInvitationModal}
/>

<!-- Remove User Confirmation Modal -->
<ConfirmationModal
  isOpen={showRemoveUserConfirmation}
  title={$_('settings.teamUsers.removeUser.title')}
  message={$_('settings.teamUsers.removeUser.message', { values: { name: userToRemove?.username } })}
  onConfirm={confirmRemoveUser}
  onCancel={cancelRemoveUser}
/>


<style>
  article {
    overflow-x: hidden;
  }

  .member-row {
    color: var(--pico-text-dark);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    margin: 0.5rem 0;
    background-color: var(--pico-secondary-hover-background);
    border-radius: 8px;
  }

  .add-member-form {
    padding-top: 1rem;
  }

  .space-y-4 > * + * {
    margin-top: 1rem;
  }

  /* Accordion header styles */
  .accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  .accordion-header:hover {
    opacity: 0.8;
  }

  .accordion-header ion-icon {
    transition: transform 0.3s ease;
  }

</style>
